import * as React from "react";
import {UserTypeEnum} from "../../model/UserInfo.model";
import {withVerifyAccess} from "../common/withVerifyAccess";
import {useEffect, useState} from "react";
import PlanApi from "../../api/PlanApi";
import {AdminEditPlanComponent} from "./AdminEditPlan.component";
import {LoadingSpinner} from "../icons/LoadingSpinner.component";
import {Map as ImmutableMap} from "immutable";
import {ProductSummary} from "../../model/product/ProductSummary";
import {getSuccessData} from "../common/commonStates";
import {multipleApiRequesterWrapper} from "../common/MultipleApiRequesterWrapper";
import {adminClientsWithPlansPair, allProductsRequestPair} from "../common/RequesterPairs";
import {connect} from "react-redux";
import {ClientsAndPlans, IClientInfo} from "../../model/ClientsAndPlans.model";
import {ISelectValue} from "../common/Select.component";
import {byName} from "../../utils/listUtil";
import {IPlanAuthorization} from "../../model/PlanAuthorization.model";
import {IApplicationRootState} from "../../../applicationState";
import {useParams} from "react-router";
import {useNavigate} from "react-router-dom";
import {navigateTo} from "../../../navigateTo";
import {PlanUsersTab} from "./PlanUsersTab.component";
import {FormattedMessage} from "react-intl";
import {getTranslation} from "../../utils/translationUtil";
import {AdminClientLinkChip} from "./AdminClientLinkChip.component";

export enum AdminEditPlanTabs {
    PLAN_SETTINGS = "PLAN_SETTINGS",
    USERS = "USERS",
}

export interface IAdminEditPlanPropsFromStore {
    productUniverseSummaries: ImmutableMap<number, ProductSummary>;
    adminClientInfoState: ClientsAndPlans;
}

export type IAdminEditPlanPageProps = IAdminEditPlanPropsFromStore;

export const AdminEditPlanPage: React.FunctionComponent<IAdminEditPlanPageProps> = (props: IAdminEditPlanPageProps) => {
    const [plan, setPlan] = useState<IPlanAuthorization | undefined>(undefined);
    const [hasPortfolioAccess, setHasPortfolioAccess] = useState<boolean>(false);
    const [currentTab, setCurrentTab] = useState(AdminEditPlanTabs.PLAN_SETTINGS);
    const planSettingsTabRef = React.useRef<HTMLButtonElement>(null);
    const usersTabRef = React.useRef<HTMLButtonElement>(null);

    const navigate = useNavigate();
    const {planIdParam} = useParams();
    const planId = Number.parseInt(planIdParam!, 10);

    const clients: ISelectValue[] = props.adminClientInfoState
        .map((clientInfo: IClientInfo) => ({id: clientInfo.id, name: clientInfo.name}))
        .sort(byName)
        .valueSeq()
        .toArray();

    const getClientName = (): string => {
        if (!plan) return "";
        const clientInfo = props.adminClientInfoState.get(plan.clientId);
        return clientInfo ? clientInfo.name : "";
    };

    useEffect(() => {
        PlanApi.requestPlan(planId).then((planResponse) => {
            PlanApi.hasPortfolioAccess(planId).then((portfolioAccessResponse) => {
                setHasPortfolioAccess(portfolioAccessResponse);
                setPlan(planResponse);
            });
        });
    }, [planId]);

    const cancelEdit = () => { navigateTo("/admin-client-plan", navigate); };

    const focusTab = (tab: AdminEditPlanTabs) => {
        if (tab === AdminEditPlanTabs.PLAN_SETTINGS) {
            planSettingsTabRef.current?.focus();
            return;
        }
        usersTabRef.current?.focus();
    };

    const handleTabKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, tab: AdminEditPlanTabs) => {
        if (event.key === " " || event.key === "Enter") {
            event.preventDefault();
            setCurrentTab(tab);
            return;
        }
        if (event.key === "ArrowRight") {
            event.preventDefault();
            setCurrentTab(AdminEditPlanTabs.USERS);
            focusTab(AdminEditPlanTabs.USERS);
            return;
        }
        if (event.key === "ArrowLeft") {
            event.preventDefault();
            setCurrentTab(AdminEditPlanTabs.PLAN_SETTINGS);
            focusTab(AdminEditPlanTabs.PLAN_SETTINGS);
            return;
        }
        if (event.key === "Home") {
            event.preventDefault();
            setCurrentTab(AdminEditPlanTabs.PLAN_SETTINGS);
            focusTab(AdminEditPlanTabs.PLAN_SETTINGS);
            return;
        }
        if (event.key === "End") {
            event.preventDefault();
            setCurrentTab(AdminEditPlanTabs.USERS);
            focusTab(AdminEditPlanTabs.USERS);
        }
    };

    const getSelected = (tab: AdminEditPlanTabs) => currentTab === tab ? "selected" : "";

    const renderPlanSettings = () => {
        return plan
            ? <AdminEditPlanComponent
                products={props.productUniverseSummaries}
                selectedClientId={plan.clientId}
                onCancel={cancelEdit}
                hasPortfolioAccess={hasPortfolioAccess}
                editPlan={{plan, planId, clients}}
            />
            : <LoadingSpinner/>;
    };

    const renderTabContent = () => {
        if (currentTab === AdminEditPlanTabs.USERS) {
            return <PlanUsersTab planId={planId} planName={plan?.name || ""}/>;
        }
        return renderPlanSettings();
    };

    return (
        <div className="page-container new-common-styles admin-edit-plan-page__container">
            <div className="admin-edit-plan-page__card">
                <h1 className="admin-edit-plan-page__header">
                    <FormattedMessage id="admin.edit-plan" defaultMessage="Edit Plan" />
                </h1>
                {plan && (
                    <div className="admin-edit-plan-page__plan-header">
                        <h2 className="admin-edit-plan-page__plan-name">{plan.name}</h2>
                        <AdminClientLinkChip clientId={plan.clientId} clientName={getClientName()} />
                    </div>
                )}
                <div className="tabs__container" role="tablist"
                     aria-label={getTranslation("admin.plan-management-tabs.aria-label", "Plan management tabs")}>
                    <button id="admin-edit-plan-page__tab-plan-settings" ref={planSettingsTabRef}
                            className={`tab clickable ${getSelected(AdminEditPlanTabs.PLAN_SETTINGS)}`}
                            role="tab" aria-selected={currentTab === AdminEditPlanTabs.PLAN_SETTINGS}
                            aria-controls="tabpanel-plan-settings"
                            tabIndex={currentTab === AdminEditPlanTabs.PLAN_SETTINGS ? 0 : -1}
                            onKeyDown={(event) => handleTabKeyDown(event, AdminEditPlanTabs.PLAN_SETTINGS)}
                            onClick={() => setCurrentTab(AdminEditPlanTabs.PLAN_SETTINGS)}
                            data-testid="admin-edit-plan-page__tab-plan-settings" type="button">
                        <h6><FormattedMessage id="admin.tab.plan-settings" defaultMessage="Plan Settings" /></h6>
                    </button>
                    <button id="admin-edit-plan-page__tab-users" ref={usersTabRef}
                            className={`tab clickable ${getSelected(AdminEditPlanTabs.USERS)}`}
                            role="tab" aria-selected={currentTab === AdminEditPlanTabs.USERS}
                            aria-controls="tabpanel-users"
                            tabIndex={currentTab === AdminEditPlanTabs.USERS ? 0 : -1}
                            onKeyDown={(event) => handleTabKeyDown(event, AdminEditPlanTabs.USERS)}
                            onClick={() => setCurrentTab(AdminEditPlanTabs.USERS)}
                            data-testid="admin-edit-plan-page__tab-users" type="button">
                        <h6><FormattedMessage id="admin.tab.users" defaultMessage="Users" /></h6>
                    </button>
                </div>
                <div role="tabpanel"
                     id={currentTab === AdminEditPlanTabs.USERS ? "tabpanel-users" : "tabpanel-plan-settings"}
                     aria-labelledby={currentTab === AdminEditPlanTabs.USERS ? "admin-edit-plan-page__tab-users" : "admin-edit-plan-page__tab-plan-settings"}>
                    {renderTabContent()}
                </div>
            </div>
        </div>
    );
};

export const mapStateToProps = (state: IApplicationRootState): IAdminEditPlanPropsFromStore => {
    return {
        productUniverseSummaries: getSuccessData(state.allProductsStore!.productUniverseSummaries)!,
        adminClientInfoState: getSuccessData(state.adminState!.adminClientInfoState)!,
    };
};

const connected =
    connect<IAdminEditPlanPropsFromStore, {}, {}, IApplicationRootState>(
        mapStateToProps,
    )(AdminEditPlanPage);

const withRequester = multipleApiRequesterWrapper(connected, [
    allProductsRequestPair(),
    adminClientsWithPlansPair,
]);

export default withVerifyAccess(withRequester, UserTypeEnum.ADMIN);
